import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCustomerAuth } from "../lib/customerAuth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sparkle, UserPlus, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatErr } from "../lib/api";
import GlobalHeader from "../components/layout/GlobalHeader";
import PasswordInput from "./components/PasswordInput";

export default function CustomerSignup() {
  const navigate = useNavigate();
  const { signup } = useCustomerAuth();
  const [form, setForm] = useState({
    email: "", password: "", name: "", phone: "",
    language: "es", marketing_consent: false,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("La contraseña debe tener al menos 8 caracteres");
    setBusy(true);
    try {
      await signup(form);
      toast.success("Cuenta creada. Bienvenido a AutoGlow.");
      // Fase 2C · redirect al onboarding cliente (skip-friendly)
      navigate("/cliente/onboarding");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "No se pudo crear la cuenta");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex flex-col">
      <GlobalHeader variant="auto" testId="customer-signup-header" showRegisterCta={false} />
      <div className="flex-1 flex items-start sm:items-center justify-center px-4 py-6 sm:py-10">
      <div className="w-full max-w-md">
        <Link to="/marketplace" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[var(--ag-gold)] mb-4" data-testid="cs-back">
          <ArrowLeft size={12} /> Volver al Marketplace
        </Link>
        <div className="bg-white border border-gray-200 rounded-md p-8 shadow-lg" data-testid="customer-signup-card">
          <div className="text-center mb-6">
            <Sparkle size={36} weight="duotone" className="text-[var(--ag-gold)] mx-auto mb-2" />
            <h1 className="font-display text-3xl font-black tracking-tighter text-[#1A1A1A]">Crear cuenta</h1>
            <p className="text-xs text-gray-500 mt-1">Una sola cuenta para reservar y chatear con todos los talleres del Marketplace.</p>
          </div>
          <form onSubmit={submit} className="space-y-3" data-testid="customer-signup-form">
            <div>
              <Label className="text-xs uppercase tracking-wider">Nombre completo *</Label>
              <Input required value={form.name}
                onChange={(e)=>setForm({...form, name: e.target.value})}
                className="mt-1 rounded-sm" data-testid="cs-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Email *</Label>
              <Input type="email" required value={form.email}
                onChange={(e)=>setForm({...form, email: e.target.value})}
                className="mt-1 rounded-sm" autoComplete="email" data-testid="cs-email" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Teléfono</Label>
              <Input type="tel" value={form.phone}
                onChange={(e)=>setForm({...form, phone: e.target.value})}
                className="mt-1 rounded-sm" autoComplete="tel" data-testid="cs-phone" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Contraseña (mín 8 caracteres) *</Label>
              <PasswordInput required minLength={8} value={form.password}
                onChange={(e)=>setForm({...form, password: e.target.value})}
                className="mt-1 rounded-sm" autoComplete="new-password" testId="cs-password" />
            </div>
            <label className="flex items-start gap-2 text-[11px] text-gray-600 cursor-pointer pt-1">
              <input type="checkbox" checked={form.marketing_consent}
                onChange={(e)=>setForm({...form, marketing_consent: e.target.checked})}
                className="mt-0.5 accent-[#0033A0]" data-testid="cs-marketing" />
              <span>Acepto recibir ofertas y promociones de los talleres con los que interactúo.</span>
            </label>
            <Button type="submit" disabled={busy}
              className="w-full bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm font-bold h-11"
              data-testid="cs-submit">
              <UserPlus size={14} weight="bold" className="mr-1.5" />
              {busy ? "Creando…" : "Crear mi cuenta"}
            </Button>
          </form>
          <div className="text-center mt-5 pt-5 border-t border-gray-100 text-xs">
            ¿Ya tienes cuenta?{" "}
            <Link to="/cliente/login" className="text-[var(--ag-gold)] font-semibold hover:underline" data-testid="cs-to-login">
              Entrar
            </Link>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
